// Third-party packages
import React, { createContext, useState, useEffect } from 'react'

// Main packages
import * as api from '../api'
import { getSessionId } from '../api'

export const AppContext = createContext()

export const Provider = (props) => {

  const [loading, setLoading] = useState(false)
  
  // Customer authentication state
  const [customerInfo, setCustomerInfo] = useState(null)
  
  // Enhanced loading state with tracking
  const setLoadingWithTracking = (isLoading, context = '') => {
    setLoading(isLoading);
    if (window.faroTracker && context) {
      window.faroTracker.trackEvent('loading_state_change', {
        loading: isLoading,
        context,
        timestamp: Date.now()
      });
    }
  }

  /*
  cart = {
    size: INTEGER,
    subtotal: INTEGER,
    items: [
      {
        id: STRING,
        quantity: INTEGER,
        product: OBJECT
      }
    ]
  }
  */
  const [cart, setCart] = useState({
    size: null,
    subtotal: null,
    items: null
  })

  /**
   * Get cart data with enhanced tracking.
   */
  const refreshCart = async (trackOperation = false) => {
    const startTime = Date.now();
    
    try {
      const cartRes = await api.cartGet()
      const quantities = cartRes.data.data || {}
      
      // Filter out items with quantity 0 or less
      const productIds = Object.keys(quantities).filter(productId => {
        const quantity = parseInt(quantities[productId]) || 0;
        return quantity > 0;
      });
      
      if (!productIds.length) {
        const emptyCart = {
          size: 0,
          subtotal: 0.0,
          items: []
        };
        setCart(emptyCart);
        
        if (window.faroTracker && trackOperation) {
          // Use setTimeout to make tracking non-blocking
          setTimeout(() => {
            try {
              window.faroTracker.trackEvent('cart_refreshed', {
                cart_size: 0,
                cart_value: 0,
                items_count: 0,
                duration: Date.now() - startTime,
                empty_cart: true
              });
            } catch (error) {
              // Silently fail - tracking should never block
              console.warn('Faro empty cart tracking failed:', error);
            }
          }, 0);
        }
        return
      }
      
      const items = []
      var size = 0
      var subtotal = 0.0
      const prodRes = await api.productDocuments(productIds)
      console.log(prodRes)
      const prodData = prodRes.data || []
      
      // Track product categories in cart
      const categories = new Set();
      const productTypes = new Set();
      
      for (var i in prodData) {
        let product = prodData[i]
        const quantity = parseInt(quantities[product.id]) || 0;
        
        // Only include items with quantity > 0
        if (quantity <= 0) {
          continue;
        }
        
        const price = parseFloat(product.price) || 0;
        const itemTotal = price * quantity;
        
        size = size + quantity;
        subtotal = subtotal + itemTotal;
        
        // Track product analytics
        categories.add(product.category || 'uncategorized');
        productTypes.add(product.type || 'unknown');
        
        items.push({
          id: product.id,
          quantity: quantity,
          product: product
        })
      }
      
      const cartData = {
        size: size,
        subtotal: subtotal,
        items: items
      };
      
      setCart(cartData);
      
      // Enhanced cart tracking (fire-and-forget to never block)
      if (window.faroTracker && trackOperation) {
        // Use setTimeout to make tracking non-blocking
        setTimeout(() => {
          try {
            window.faroTracker.trackEvent('cart_refreshed', {
              cart_size: size,
              cart_value: subtotal,
              items_count: items.length,
              duration: Date.now() - startTime,
              categories: Array.from(categories),
              product_types: Array.from(productTypes),
              empty_cart: false,
              avg_item_value: items.length > 0 ? subtotal / items.length : 0
            });
          } catch (error) {
            // Silently fail - tracking should never block
            console.warn('Faro cart refresh tracking failed:', error);
          }
        }, 0);
      }
      
    } catch (error) {
      console.error('Cart refresh failed:', error);
      
      // Check if it's a timeout error
      const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
      
      if (window.faroTracker) {
        // Use setTimeout to make tracking non-blocking
        setTimeout(() => {
          try {
            window.faroTracker.trackError(error, {
              context: 'cart_refresh',
              duration: Date.now() - startTime,
              error_type: isTimeout ? 'timeout' : 'unknown',
              timeout_ms: error.config?.timeout || 'unknown'
            });
          } catch (trackingError) {
            // Silently fail - tracking should never block
            console.warn('Faro error tracking failed:', trackingError);
          }
        }, 0);
      }
      
      // Don't throw error - instead set empty cart to allow app to continue
      // This prevents the app from breaking when the cart service is slow or unavailable
      setCart({
        size: 0,
        subtotal: 0.0,
        items: []
      });
      
      // Only throw if explicitly tracking the operation (user-initiated actions)
      if (trackOperation) {
        throw error;
      }
    }
  }

  /**
   * Determine whether a product ID exists in a cart
   */
  const cartHas = (productId) => {
    for (var i in (cart.items || []))
      if (cart.items[i].id == productId && cart.items[i].quantity > 0)
        return true
    return false
  }

  /**
   * Enhanced cart operation tracking
   */
  const trackCartOperation = (operation, productId, quantity = 1) => {
    if (window.faroTracker) {
      window.faroTracker.trackCartOperation(
        operation, 
        productId, 
        quantity, 
        cart.subtotal, 
        cart.size
      );
    }
  };

  /**
   * Initialize session and cart on page load.
   */
  useEffect(() => {
    // Load customer info from localStorage
    const storedCustomerInfo = api.getCustomerInfo();
    if (storedCustomerInfo) {
      setCustomerInfo(storedCustomerInfo);
    }
    
    if (cart.items === null) {
      setLoadingWithTracking(true, 'initial_cart_load');
      
      // Set up session tracking with Faro
      const sessionId = getSessionId() || 'anonymous';
      if (window.faroTracker && sessionId !== 'anonymous') {
        window.faroTracker.setCartSessionId(sessionId);
      }
      
      (async () => {
        try {
          await refreshCart(true); // Track the initial cart load
          
          // Track initial app load
          if (window.faroTracker) {
            window.faroTracker.trackEvent('app_initialized', {
              session_id: sessionId,
              has_session: sessionId !== 'anonymous',
              initial_load: true
            });
          }
        } catch (error) {
          console.error('Initial cart load failed:', error);
          
          // Set empty cart state on error to prevent UI blocking
          setCart({
            size: 0,
            subtotal: 0.0,
            items: []
          });
          
          if (window.faroTracker) {
            window.faroTracker.trackError(error, {
              context: 'initial_app_load'
            });
          }
        } finally {
          setLoadingWithTracking(false, 'initial_cart_load');
        }
      })();
    }
  }, []);

  return (
    <AppContext.Provider value={{
        cart, setCart, refreshCart, cartHas,
        loading, setLoading: setLoadingWithTracking,
        trackCartOperation, // Add cart operation tracking
        customerInfo, setCustomerInfo, // Customer authentication state
      }}>
      {props.children}
    </AppContext.Provider>
  )
}

// Hook to use the app context
export const useAppContext = () => {
  const context = React.useContext(AppContext)
  if (context === undefined) {
    throw new Error('useAppContext must be used within a Provider')
  }
  return context
}
